import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import ConversationList from '../components/ConversationList';
import ChatPane from '../components/ChatPane';
import { useConversationsViewModel } from '../viewmodels/ChatViewModel';
import { useSession } from '../viewmodels/SessionViewModel';

/** Chat workspace: conversation list on the left, the open chat on the right (one at a time on a phone). */
export default function ChatView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const { isOwner } = useSession();
  const vm = useConversationsViewModel();
  const [params] = useSearchParams();
  const botParam = params.get('bot');

  // /chat?bot=<id> opens that team member's chat (used by the org chart).
  useEffect(() => {
    if (!botParam || !vm.items.length) return;
    const target = vm.items.find((c) => c.bot?.id === botParam);
    if (target) navigate(`/chat/${target.id}`, { replace: true });
  }, [botParam, vm.items, navigate]);

  // On a wide screen open the first chat (the OrkaCrew bot) when none is chosen.
  useEffect(() => {
    if (wide && !id && !botParam && vm.items.length) navigate(`/chat/${vm.items[0].id}`, { replace: true });
  }, [wide, id, botParam, vm.items, navigate]);

  const select = (cid) => navigate(`/chat/${cid}`);
  const showList = wide || !id;
  const showPane = wide || Boolean(id);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {showList && (
        <Box sx={{ width: wide ? 340 : '100%', flexShrink: 0, minHeight: 0 }}>
          <ConversationList
            items={vm.items}
            loading={vm.loading}
            error={vm.error}
            onRetry={vm.reload}
            selectedId={id}
            onSelect={select}
            canAdd={isOwner}
          />
        </Box>
      )}
      {showPane && (
        <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0 }}>
          {id ? (
            <ChatPane
              key={id}
              conversationId={id}
              status={vm.status}
              onBack={wide ? undefined : () => navigate('/chat')}
              onActivity={vm.markReadLocally}
            />
          ) : null}
        </Box>
      )}
    </Box>
  );
}
